import { useState, type ReactNode } from 'react';

interface Props {
  hasConnections: boolean;
  onGoToSystems: () => void;
  onCreateTask: () => void;
  onGoToPriorities: () => void;
  onFinish: () => void;
  onSkip: () => void;
}

interface Step {
  title: string;
  body: ReactNode;
  cta?: { label: string; onClick: () => void };
}

/**
 * A first-run walkthrough. It's deliberately independent of any account
 * system — it triggers off a single server-side "completed" flag rather than
 * a login, so it works today and folds into a real signup flow later without
 * changing its own logic, only what sets the flag.
 */
export function OnboardingTour({ hasConnections, onGoToSystems, onCreateTask, onGoToPriorities, onFinish, onSkip }: Props) {
  const [step, setStep] = useState(0);

  const steps: Step[] = [
    {
      title: 'Welcome to Z3NTRAFY',
      body: (
        <p>
          Z3NTRAFY pulls your work from every system you use — Azure DevOps, Monday, Jira,
          Zendesk, ClickUp, LiveAgent, or a custom system you connect yourself — into one
          prioritized list, and can write your edits back to the source. This is a quick tour
          of the two things worth knowing before you dive in: connecting your systems, and
          adding and managing tasks.
        </p>
      ),
    },
    {
      title: 'Connect your systems',
      body: (
        <>
          <p>
            Head to the <strong>Systems</strong> tab to connect a source — pick one from the
            list, or use <strong>Custom / Other</strong> to map a JSON API Z3NTRAFY doesn't
            support out of the box. Once connected, <strong>Sync all</strong> pulls in
            everything assigned to you.
          </p>
          <p className="help">
            {hasConnections
              ? 'You already have at least one system connected — nice.'
              : "Don't have credentials handy right now? Skip this and add tasks manually instead — you can connect a system anytime."}
          </p>
        </>
      ),
      cta: { label: 'Take me to Systems', onClick: onGoToSystems },
    },
    {
      title: 'Add and manage tasks',
      body: (
        <>
          <p>
            Use <strong>+ New task</strong> for anything that isn't tracked anywhere else.
            Opening any task shows its full breakdown — you can set importance and effort,
            flag that someone's waiting on it, pin it to the top, snooze it, or link it to
            related work. Untriaged tasks (missing importance or effort) show up in the{' '}
            <strong>Untriaged</strong> count so nothing quietly sits unscored.
          </p>
          <p className="help">
            From a task you can also generate a document (a Demo doc, or a real Google Doc
            once connected) under its <strong>Documents</strong> section — handy for
            write-ups or status updates that should live outside Z3NTRAFY too.
          </p>
        </>
      ),
      cta: { label: 'Create my first task', onClick: onCreateTask },
    },
    {
      title: 'How the ranking works',
      body: (
        <>
          <p>
            Every task gets a 0–100 score from eight weighted factors — urgency, importance,
            who's waiting, impact, promises, age, quick wins, and org priority. Opening a
            task shows exactly which factors contributed and why, so the order is always
            explainable, not a black box.
          </p>
          <p className="help">
            Under <strong>Priorities</strong> you can pick a role preset (manager, IC,
            exec…) or hand-tune the weights, plus manage projects and org priorities that
            feed the "org priority" factor.
          </p>
        </>
      ),
      cta: { label: 'Show me Priorities', onClick: onGoToPriorities },
    },
  ];

  const current = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <div className="overlay tour-overlay" role="dialog" aria-modal="true" aria-label="Getting started with Z3NTRAFY">
      <div className="modal tour-modal">
        <div className="modal-head">
          <h3>{current.title}</h3>
          <div className="spacer" />
          <button type="button" className="btn ghost sm" onClick={onSkip}>
            Skip tour
          </button>
        </div>
        <div className="modal-body">
          {current.body}
          <div className="tour-dots" aria-hidden>
            {steps.map((_, i) => (
              <span key={i} className={`tour-dot ${i === step ? 'active' : ''}`} />
            ))}
          </div>
        </div>
        <div className="modal-foot">
          {step > 0 && (
            <button type="button" className="btn ghost" onClick={() => setStep((s) => s - 1)}>
              Back
            </button>
          )}
          <div className="spacer" />
          {current.cta && (
            <button type="button" className="btn" onClick={current.cta.onClick}>
              {current.cta.label}
            </button>
          )}
          {isLast ? (
            <button type="button" className="btn primary" onClick={onFinish}>
              Done
            </button>
          ) : (
            <button type="button" className="btn primary" onClick={() => setStep((s) => s + 1)}>
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
